import { PropsWithChildren, useContext, useState, useEffect } from "react";
import { AuthenticationContext } from "core/iam/AuthenticationProvider";
import { whoAmI } from "generated/api/iam/iam";
import { CenteredLoading } from "foundations/CenteredLoading/CenteredLoading";
import globalAxios, { AxiosError } from "axios";
import { axios } from "utils/axios";
import { AXIOS_INSTANCE } from "lib/SpringAxios";

/**
 * Makes an API call to check if there's an existing session, and if so sets the authentication
 * context with the retrieved user data.
 */
export function SessionDetector(props: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const { login, logout } = useContext(AuthenticationContext);
  // Check if there's an existing session and set the authentication context if so
  const checkSession = async () => {
    try {
      const user = await whoAmI();
      login(user);
    } catch (error: unknown) {
      // @ts-expect-error -- annoying ass requirement that error be type `unknown`
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (error?.response?.status === 401) {
        await logout();
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    void checkSession();
  }, []);

  useEffect(() => {
    // Check authentication if Axios gets a 401 response
    async function errorHandler(error: AxiosError) {
      // Handle 401 error on any endpoint that is not a session endpoint (e.g. login, password reset)
      if (
        error.response?.status === 401 &&
        !error.config?.url?.includes("sessions") &&
        !error.config?.url?.includes("api/v2/internal/csrf-token") &&
        !error.config?.url?.includes("logout") &&
        !error.config?.url?.includes("whoami") &&
        !error.config?.url?.includes("user_data") &&
        !error.config?.url?.includes("auth/login")
      ) {
        await checkSession();
        return null;
      }
      return Promise.reject(error);
    }
    axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => errorHandler(error),
    );
    AXIOS_INSTANCE.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => errorHandler(error),
    );
    globalAxios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => errorHandler(error),
    );
  }, []);

  // Add event listeners for tab visibility and network changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only recheck if the tab becomes visible after being hidden
      if (document.visibilityState === "visible") {
        void checkSession();
      }
    };

    const handleOnline = () => {
      // Recheck when coming back online
      void checkSession();
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    // Clean up
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  if (isLoading) {
    return <CenteredLoading />;
  }

  return props.children;
}
